import { ArrowLeft, Eye } from "lucide-react";

/**
 * ImpersonationBanner — renders a sticky top bar when the agent is
 * previewing the portal "as this client". The Back button clears every
 * portal-side localStorage key we own and navigates the browser to the
 * saved agent path (usually /clients), giving the agent a one-click exit.
 *
 * Rendered inside PortalShell so it appears above every portal page.
 * Only shows when localStorage flag `realtor_os_impersonate_active` is set.
 */

const PORTAL_KEYS = [
  "realtor_os_portal_token",
  "realtor_os_portal_agent",
  "realtor_os_portal_client",
  "realtor_os_portal_brand",
];
const IMPERSONATE_RETURN_KEY = "realtor_os_impersonate_return_path";
const IMPERSONATE_ACTIVE_KEY = "realtor_os_impersonate_active";

export function isImpersonating() {
  try { return localStorage.getItem(IMPERSONATE_ACTIVE_KEY) === "1"; }
  catch { return false; }
}

export default function ImpersonationBanner({ clientName }) {
  if (!isImpersonating()) return null;

  const exit = () => {
    const returnPath = localStorage.getItem(IMPERSONATE_RETURN_KEY) || "/clients";
    for (const k of PORTAL_KEYS) localStorage.removeItem(k);
    localStorage.removeItem(IMPERSONATE_RETURN_KEY);
    localStorage.removeItem(IMPERSONATE_ACTIVE_KEY);
    // Full navigation (not React Router push) so the portal shell fully
    // tears down and any cached in-memory portal state is dropped.
    window.location.assign(returnPath);
  };

  return (
    <div
      className="sticky top-0 z-[80] w-full text-white shadow-md"
      style={{ background: "linear-gradient(90deg, #0F172A 0%, #1E293B 100%)" }}
      data-testid="impersonation-banner"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-11 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 shrink-0 text-amber-300" />
          <div className="min-w-0 text-xs sm:text-sm">
            <span className="font-semibold">Previewing as</span>
            <span className="ml-1.5 truncate">{clientName || "your client"}</span>
            <span className="hidden sm:inline text-white/60 ml-2">· agents only see this banner</span>
          </div>
        </div>
        <button
          type="button"
          onClick={exit}
          className="inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1 text-xs font-semibold ring-1 ring-white/20"
          data-testid="impersonation-exit-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to agent view
        </button>
      </div>
    </div>
  );
}
